import React, { useEffect, useState } from 'react';
import axios from "axios";
import { styled, useTheme } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import { Outlet } from 'react-router-dom'; // Add this import
import Sidebar from './Sidebar';
import Footer from './Footer';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import backendURL, { google_login_api, SETUP_STEP_LOCAL_KEY, SETUP_COMPLETE_LOCAL_KEY } from "../config";

// --- Configuration ---
const API_BASE_URL = backendURL;

const drawerWidth = 300;

// --- Styled Components ---
const LayoutRoot = styled(Box)({
    display: 'flex',
    minHeight: '100vh',
});

const MainAreaContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isCollapsed' && prop !== 'isMobile'
})(({ theme, isCollapsed, isMobile }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginLeft: !isCollapsed && !isMobile ? `${drawerWidth}px` : 0,
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...( !isMobile && !isCollapsed && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
}));

const FooterWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
}));

function Layout({ isCollapsed, onToggleSidebar }) { // Note: Removed `children` prop since it's not needed
    const navigate = useNavigate();
    const accessToken = localStorage.getItem('access_token');
    const userData = JSON.parse(localStorage.getItem('user'));

    const theme = useTheme();
    const [isMobileView, setIsMobileView] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/Login');
        }
    }, []);

    useEffect(() => {
        const checkAuthAndSubscription = async () => {
            if (!accessToken) {
                navigate('/Login');
                return;
            }
            try {
                const headers = { Authorization: `Bearer ${accessToken}` };
                const subPromise = axios.get(`${API_BASE_URL}/subscriptions/current`, { headers });
                const [subResult] = await Promise.allSettled([subPromise]);
                
                let fetchedSubscription = null;

                if (subResult.status === 'fulfilled' && subResult.value?.data) {
                    fetchedSubscription = subResult.value.data.data || subResult.value.data;
                    if (!fetchedSubscription || !fetchedSubscription.plan) {
                        if (fetchedSubscription?.status !== 'freemium') {
                            fetchedSubscription = null;
                        }
                    }
                } else if (subResult.reason?.response?.status === 404) {
                    const responseData = subResult.reason.response.data;
                    fetchedSubscription = responseData?.data?.plan?.name === 'freemium' ? responseData.data : null;
                } else {
                    console.error("Subscription fetch failed:", subResult.reason);
                }
                
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const updatedUserData = JSON.parse(userStr);
                    updatedUserData.subscription = fetchedSubscription;
                    localStorage.setItem('user', JSON.stringify(updatedUserData));
                }

                if (!fetchedSubscription) {
                    localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
                    navigate('/SignUp-Process-Steps');
                }
            } catch (err) {
                console.error("Unexpected error during subscription fetch:", err);
            }
        };
    
        checkAuthAndSubscription();
    }, [accessToken, userData, navigate]);

    useEffect(() => {
        setIsMobileView(isMobile);
        setIsSidebarOpen(!isMobile);
    }, [isMobile]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleMobileSidebarClose = () => {
        if (isMobileView) {
            setIsSidebarOpen(false);
        }
    };

    if (!accessToken) {
        return null;
    }

    return (
        <LayoutRoot>
            <CssBaseline />
            <Header toggleSidebar={toggleSidebar} isSidebarCollapsed={!isSidebarOpen} isMobile={isMobileView} />
            <Sidebar
                isCollapsed={isSidebarOpen}
                isOpen={isSidebarOpen}
                drawerWidth={drawerWidth}
                onClose={handleMobileSidebarClose}
                isMobile={isMobileView}
            />
            <MainAreaContainer isCollapsed={isSidebarOpen} isMobile={isMobileView}>
                <ContentWrapper>
                    <Outlet /> {/* Replace {children} with <Outlet /> */}
                </ContentWrapper>
                <FooterWrapper>
                    <Footer />
                </FooterWrapper>
            </MainAreaContainer>
        </LayoutRoot>
    );
}

export default Layout;